<template>
  <div>
    <acis-structure
        :dealName="dealName"
        :structure="structure"
        @showStructure="showStructure"
        @inputError="$emit('inputError', $event)"
        @showSpinner="ObjSpinnerState.isOn = true;"
        @hideSpinner="ObjSpinnerState.isOn = false;"
        @runAllBondsCalculation="runAllBondsCalculation"
    />
    <b-card no-body class="mb-1">
      <b-card-header header-tag="header" class="p-0 mt-1">
        <b-btn block href="#" v-b-toggle.acis variant="info" class="text-left">{{ bondName }}, {{ dealName }} - ACIS</b-btn>
      </b-card-header>
      <b-collapse id="acis" visible>
        <b-card style="border: none">
          <b-row class="mb-3">
            <b-col cols="3">
              <settings
                  :inputOwnedFaceAmt="inputOwnedFaceAmt"
                  :inputMargin="inputMargin"
                  :inputInterestRate="inputInterestRate"
                  @ownedFaceAmtChange="inputOwnedFaceAmt = $event"
                  @marginChange="inputMargin = $event"
                  @interestRateChange="inputInterestRate = $event"/>
              <b-row class="float-right mb-3">
                <b-col cols="12" class="text-right">
                  <b-btn variant="primary" class="ml-1 mb-1" v-on:click="runCalculation(true)">Run scenario</b-btn>
                  <b-btn variant="primary" class="ml-1 mb-1" v-on:click="runCalculation(false)">Run Matrix</b-btn>
                  <b-btn variant="primary" class="ml-1 mb-1" v-on:click="runAllBondsCalculation(false)">Run All Bonds Scenario</b-btn>
                </b-col>
              </b-row>
              <b-row>
                <b-alert v-if="this.errorMessage" variant="danger" class="float-left" show>{{ this.errorMessage }}</b-alert>
              </b-row>
              <SingleScenarioResult
                  caption="ACIS"
                  type="R-O-I"
                  :result="this.yield"
                  :wal="wal"
                  :principalWindow="principalWindow"
                  :writedownPct="writedownPct"
                  :firstLossPeriod="firstLossPeriod"/>
              <LineMetrics :lineMetrics="lineMetrics"/>
            </b-col>
            <b-col cols="9">
              <b-tabs>
                <b-tab @click="clearErrorMessage()" title="ROI" active>
                  <matrix :matrixName="'ROI'" :matrixUnits="'%'" :cpr="matrixCpr" :cdr="matrixCdr"
                          :matrix="roiMatrix" @matrixCellClicked="onMatrixCellClicked" :vectors="vectorNames"/>
                </b-tab>
                <b-tab @click="clearErrorMessage()" title="Rate On Line">
                  <matrix
                      :matrixName="'Rate On Line'"
                      :matrixUnits="'%'"
                      :cpr="matrixCpr"
                      :cdr="matrixCdr"
                      :matrix="rateOnLineMatrix"
                      @matrixCellClicked="onMatrixCellClicked"
                      :vectors="vectorNames"/>
                </b-tab>
                <b-tab @click="clearErrorMessage()" title="Loss On Line">
                  <matrix
                      :matrixName="'Loss On Line'"
                      :matrixUnits="'%'"
                      :cpr="matrixCpr"
                      :cdr="matrixCdr"
                      :matrix="lossOnLineMatrix"
                      @matrixCellClicked="onMatrixCellClicked"
                      :vectors="vectorNames"/>
                </b-tab>
                <b-tab @click="clearErrorMessage()" title="Margin On Line">
                  <matrix :matrixName="'Margin On Line'" :matrixUnits="'%'" :cpr="matrixCpr" :cdr="matrixCdr"
                          :matrix="returnOnLineMatrix" @matrixCellClicked="onMatrixCellClicked" :vectors="vectorNames"/>
                </b-tab>
              </b-tabs>
            </b-col>
          </b-row>
          <div>
            <b-row class="mb-2 mt-2 ml-0">
              <b-btn v-if="acisCashflow.length > 0" variant="info" class="float-left" @click="exportCashflowToExcel()">Export Cashflow to Excel</b-btn>
            </b-row>
            <cashflow
                ref="cashflow"
                :acisCashflow="acisCashflow"
                :miscVariables="miscVariables"
                :totals="totals"
                :dealName="dealName"
                :bondName="bondName"
                :isMatrixClicked="matrixCellClicked"
                :cpr="cprToPass"
                :cdr="cdrToPass"/>
          </div>

          <!-- ################################################## -->
          <!-- region All Bonds Cashflow.-->
          <div v-if="cashflowAllBonds.length > 0" class="text-left">
            <b-row class="mb-2 mt-2 ml-0">
              <b-btn variant="info" class="float-left" @click="exportCashflowAllBondsToExcel()">Export All Bonds Cashflow to Excel</b-btn>
            </b-row>
            <b-tabs content-class="mt-3">
              <b-tab
                  v-for="eachBond in cashflowAllBonds"
                  :key="eachBond.bondName"
                  :title="eachBond.bondName"
              >
                <cashflow
                    ref="refAllBondsCashflow"
                    :acisCashflow="eachBond.acisCashflow"
                    :miscVariables="eachBond.miscVariables"
                    :totals="eachBond.totals"
                    :dealName="eachBond.dealName"
                    :bondName="eachBond.bondName"
                    :isMatrixClicked="eachBond.matrixCellClicked"
                    :cpr="eachBond.cprToPass"
                    :cdr="eachBond.cdrToPass"/>
              </b-tab>
            </b-tabs>
          </div>
          <!-- endregion All Bonds Cashflow.-->
          <!-- ################################################## -->
        </b-card>
      </b-collapse>
    </b-card>
  </div>
</template>

<script>
import service                                        from '@/services/HttpRequestService';
import formatter                                      from '@/utilities/Formatter';
import {convertToPctArray}                            from '@/utilities/ArrayUtils';
import {createEmpty2dArray}                           from '@/utilities/ArrayUtils';
import {isNotEmpty2dArray}                            from '@/utilities/ArrayUtils';
import moment                                         from 'moment';
import Datepicker                                     from 'vue2-datepicker';
import DealCatalog                                    from '../dealCatalog';
import SingleScenarioResult                           from '@/components/SingleScenarioResult';
import LineMetrics                                    from '../LineMetrics';
import Matrix                                         from '../Matrix';
import Settings                                       from './Settings';
import Cashflow                                       from './Cashflow';
import AcisStructure                                  from './AcisStructure';
import {isAcisAnalysisBodyValid}                      from '@/validators/inputValidator';
import {createAcisValidationErrorMessage}             from '@/validators/inputValidator';
import RequestBodyFactory                             from '../../services/RequestBodyFactory';
import {loadValuesFromVector, prepareScenarioVectors} from '@/utilities/SimulationVectors';
import excelSupport                                   from '../../utilities/ExcelSupport';
import {TRACE_ERROR}                                  from '@/constants';
import ObjSpinnerState                                from "@/components/common/spinner/ObjSpinnerState";
import UtilsData                                      from "@/utilities/UtilsData";

export default {
  props: [
    'bonds',
    'dealName',
    'bondName',
    'settlementDate',
    'cpr',
    'cdr',
    'lossSeverity',
    'delinquency',
    'libor',
    'cancel',
    'hpi',
    'NationalUnemploymentForecast',
    'hpa',
    'twoYrTsy',
    'tenYrTsy',
    'tuneCpr',
    'tuneCdr',
    'tuneSeverity',
    'mtg15Yr',
    'mtg30Yr',
    'selectedScenario'
  ],
  data() {
    return {
      ObjSpinnerState,
      inputOwnedFaceAmt:  '',
      inputMargin:        '',
      inputInterestRate:  0.0,
      acisCashflow:       [],
      miscVariables:      [],
      cashflowAllBonds:   [],
      errorMessage:       '',
      yield:              '',
      wal:                '',
      principalWindow:    '',
      firstLossPeriod:    '',
      writedownPct:       '',
      matrixCpr:          [1, 2, 3],
      matrixCdr:          [1, 2, 3],
      roiMatrix:          [[]],
      rateOnLineMatrix:   [[]],
      lossOnLineMatrix:   [[]],
      returnOnLineMatrix: [[]],
      totals:             {},
      rateOnLine:         '',
      lossOnLine:         '',
      returnOnLine:       '',
      forceUpdate:        false,
      structure:          {},
      matrixCellClicked:  false,
      cprToPass:          0,
      cdrToPass:          0,
      runDate:            null,
      vectors:            new Map(),
      vectorNames:        []
    };
  },
  async created() {
    readCookies(this);
    setDataFromAppData(this);
    let res = await service.getUserVectors();
    if (res.status == 200) {
      let vectors = res.data;
      for (let vector of vectors) {
        this.vectors.set(vector.Name, vector.Value.join(','));
        this.vectorNames.push(vector.Name);
      }
    }
  },

  watch: {
    bondName(newVal) {
      if (this.$store.getters.bondName != newVal) {
        this.$store.commit('saveAcisSingleResult', {AcisCashflow: []});
        this.$store.commit('saveAcisMatrixResult', createEmpty2dArray(8));
        setDataFromAppData(this);
      }
      this.$store.commit('setBondName', newVal);
    }
  },

  methods:    {
    async runCalculation(
        isSingle,
        cpr           = null,
        cdr           = null,
        _AcisBondName = false,
        _ownedFaceAmt = null, /*Initial Limit*/
        _margin       = null /*Collateral*/
    ) {
      if (cpr == null && cdr == null) {
        this.matrixCellClicked = false;
      }
      this.clearErrorMessage();
      this.$emit('inputError', '');
      this.clearResults();

      let cprToPass = [];
      let cdrToPass = [];

      // ##################################################
      // On matrix cell is clicked*/
      if (isSingle && cpr != null) {
        const regex = RegExp('.*[a-zA-Z]+.*');
        if (typeof cpr === 'string' && regex.test(cpr)) {
          if (this.vectors.has(cpr)) {
            cpr = this.vectors.get(cpr);
          } else {
            this.errorMessage = cpr + ' vector was not found.';
            return;
          }
        }
        if (typeof cdr === 'string' && regex.test(cdr)) {
          if (this.vectors.has(cdr)) {
            cdr = this.vectors.get(cdr);
          } else {
            this.errorMessage = cdr + ' vector was not found.';
            return;
          }
        }
      }
      // ##################################################
      else {
        const regex = RegExp('.*[a-zA-Z]+.*');
        for (let i = 0; i < this.matrixCpr.length; i++) {
          if (typeof this.matrixCpr[i] === 'string' && regex.test(this.matrixCpr[i])) {
            if (this.vectors.has(this.matrixCpr[i])) {
              cprToPass.push(this.vectors.get(this.matrixCpr[i]));
            } else {
              this.errorMessage = this.matrixCpr[i] + ' vector was not found.';
              return;
            }
          } else {
            cprToPass.push(this.matrixCpr[i]);
          }
        }

        for (let i = 0; i < this.matrixCdr.length; i++) {
          if (typeof this.matrixCdr[i] === 'string' && regex.test(this.matrixCdr[i])) {
            if (this.vectors.has(this.matrixCdr[i])) {
              cdrToPass.push(this.vectors.get(this.matrixCdr[i]));
            } else {
              this.errorMessage = this.matrixCdr[i] + ' vector was not found.';
              return;
            }
          } else {
            cdrToPass.push(this.matrixCdr[i]);
          }
        }
      }

      try {
        // ###############
        // Initial Limit
        let ownedFaceAmt = !UtilsData.isEmpty(_ownedFaceAmt) ? _ownedFaceAmt : this.inputOwnedFaceAmt;
        ownedFaceAmt     = UtilsData.castToNumber(ownedFaceAmt);
        // ###############
        // Collateral
        let margin           = !UtilsData.isEmpty(_margin) ? _margin : this.inputMargin;
        margin               = UtilsData.castToNumber(margin);
        margin               = margin / 100;
        // ###############
        let reqBody          = new RequestBodyFactory(
            'ACIS',
            {
              dealName:         this.dealName,
              settlementDate:   this.settlementDate,
              runLoanLevel:     this.$store.getters.useLoanLevel,
              prepaymentModel:  this.$store.getters.prepaymentModel,
              cpr:              isSingle ? cpr || this.cpr : cprToPass,
              cdr:              isSingle ? cdr || this.cdr : cdrToPass,
              lossSeverity:     this.lossSeverity,
              delinquency:      this.delinquency,
              hpi:              this.hpi,
              NationalUnemploymentForecast: this.NationalUnemploymentForecast,
              hpa:              this.hpa,
              twoYrTsy:         this.twoYrTsy,
              tenYrTsy:         this.tenYrTsy,
              tuneCpr:          this.tuneCpr,
              tuneCdr:          this.tuneCdr,
              tuneSeverity:     this.tuneSeverity,
              mtg15:            this.mtg15Yr,
              mtg30:            this.mtg30Yr,
              selectedScenario: this.selectedScenario,
              bondName:         _AcisBondName ? _AcisBondName : this.bondName,
              ownedFaceAmt:     ownedFaceAmt,
              margin:           margin,
              interestRate:     this.inputInterestRate / 100,
              cancellation:     this.cancel
            },
            isSingle
        );
        ObjSpinnerState.isOn = true;
        this.runDate         = new Date();
        let calcResult;
        /**
         * This If block runs, when
         * - Run Scenario
         * - Run All Bonds Scenario
         * - A Matrix-Cell is clicked
         * */
        if (isSingle) {
          calcResult = await this.runSingleCalculation(reqBody);
          if (_AcisBondName) {
            return calcResult;
          } else {
            Object.assign(this, calcResult);
            this.$store.commit('saveAcisSingleResult', {
              Yield:              calcResult.yield,
              Wal:                calcResult.wal,
              PrincipalWindow:    calcResult.principalWindow,
              WritedownPct:       calcResult.writedownPct,
              FirstLossPeriod:    calcResult.firstLossPeriod,
              AcisCashflow:       calcResult.acisCashflow,
              AcisCashflowTotals: calcResult.totals,
              LineMetrics:        calcResult.lineMetrics
            });
          }
        }
        /** Matrix */
        else {
          calcResult = await this.runMatrixCalculation(reqBody);
          Object.assign(this, calcResult);
          this.$store.commit('saveAcisMatrixResult', {
            RoiMatrix:          calcResult.roiMatrix,
            RateOnLineMatrix:   calcResult.rateOnLineMatrix,
            LossOnLineMatrix:   calcResult.lossOnLineMatrix,
            ReturnOnLineMatrix: calcResult.returnOnLineMatrix
          });
        }
      } catch (err) {
        this.errorMessage = err.response.data.Message;
        await service.log(TRACE_ERROR, this.errorMessage, err.stack);
      } finally {
        ObjSpinnerState.isOn = false;
      }
    },

    async runSingleCalculation(body) {
      let errorMessage;
      this.cashflowAllBonds = [];

      if (body.PrepaymentModel == 'UserDefined' && !isAcisAnalysisBodyValid(body)) {
        errorMessage = createAcisValidationErrorMessage(
            body.Cpr,
            body.Cdr,
            body.LossSeverity,
            body.Delinquency,
            body.OwnedFaceAmt,
            body.Margin,
            body.InterestRate,
            true
        );
        return;
      }
      let res   = await service.post('simulate/acis', body);
      let value = '';

      let acisCashflow = formatter.formatDataInArray(
          res.data.AcisCashflow,
          [
            'RemainingLiability',
            'ReductionInLiability',
            'InsurerPaidLosses',
            'InsurancePremium',
            'CollateralTrustAccount',
            'CollateralAccountChange',
            'CollateralAccountInterest',
            'NetInsuranceCashflow',
            'CollateralBalance',
            'CollateralLosses'
          ],
          ['PaymentDate'],
          ['CprRate', 'CdrRate', 'LossSeverityRate', 'DelinquencyRate']
      );

      let miscVariables = res.data.MiscVariables;

      let wal             =
              res.data.Wal != null
                  ? formatter.round(res.data.Wal)
                  : formatter.formatDashesIfNeeded(res.data.Wal);
      let principalWindow = formatter.formatDashesIfNeeded(res.data.ReductionInLiabilityWindow);
      let writedownPct    =
              res.data.InsurerPaidLosses != null
                  ? formatter.formatPctValue(res.data.InsurerPaidLosses)
                  : formatter.formatDashesIfNeeded(res.data.InsurerPaidLosses);
      let firstLossPeriod = res.data.FirstLossPeriod;
      let totals          = {
        ReductionInLiability: formatter.formatDollarValue(res.data.AcisCashflowTotals.ReductionInLiability),
        InsurerPaidLosses:    formatter.formatDollarValue(res.data.AcisCashflowTotals.InsurerPaidLosses),
        InsurancePremium:     formatter.formatDollarValue(res.data.AcisCashflowTotals.InsurancePremium),
        CollateralLosses:     formatter.formatDollarValue(res.data.AcisCashflowTotals.CollateralLosses),
        NetInsuranceCashflow: formatter.formatDollarValue(res.data.AcisCashflowTotals.NetInsuranceCashflow)
      };
      let lineMetrics     = {
        rateOnLine:   formatter.formatPctValue(res.data.LineMetrics.RateOnLine),
        lossOnLine:   formatter.formatPctValue(res.data.LineMetrics.LossOnLine),
        returnOnLine: formatter.formatPctValue(res.data.LineMetrics.ReturnOnLine)
      };

      if (res.data.Yield.IsSuccessful) {
        value = formatter.formatPctValue(res.data.Yield.Value);
      } else {
        value = '-----';
        // TODO sk: remove after redesigning c++ message system
        if (
            res.data.Yield.ErrorMessage !== "Can't compute Yield." &&
            res.data.Yield.ErrorMessage !== 'Too many tries in Yield calculation.' &&
            res.data.Yield.ErrorMessage !== 'Guess is too negative in Yield calculation.' &&
            res.data.Yield.ErrorMessage !== "Can't compute Spread." &&
            res.data.Yield.ErrorMessage !== 'Too many tries in Spread calculation.'
        ) {
          errorMessage = res.data.Yield.ErrorMessage;
        }
      }
      let Yield = value;

      return {
        errorMessage,
        acisCashflow,
        miscVariables,
        wal,
        principalWindow,
        writedownPct,
        firstLossPeriod,
        totals,
        lineMetrics,
        yield: Yield
      };
    },

    async runMatrixCalculation(body, matrixCdr = this.matrixCdr, matrixCpr = this.matrixCpr) {
      let roiMatrix          = createEmpty2dArray(8);
      let rateOnLineMatrix   = createEmpty2dArray(8);
      let lossOnLineMatrix   = createEmpty2dArray(8);
      let returnOnLineMatrix = createEmpty2dArray(8);

      let errorMessage;
      try {
        this.cashflowAllBonds = [];

        if (!isAcisAnalysisBodyValid(body)) {
          errorMessage = createAcisValidationErrorMessage(
              body.Cpr,
              body.Cdr,
              body.LossSeverity,
              body.Delinquency,
              body.OwnedFaceAmt,
              body.Margin,
              body.InterestRate,
              false
          );
          return;
        }

        let res         = await service.post('simulate/acis/matrix', body);
        let yields      = res.data.Yield;
        let lineMetrics = res.data.LineMetrics;

        let cprIndex = 0;
        let cdrIndex = 0;
        for (let matrixCdrIndex = 0; matrixCdrIndex < matrixCdr.length; matrixCdrIndex++) {
          if (
              matrixCdr[matrixCdrIndex] === '' ||
              matrixCdr[matrixCdrIndex] == undefined
          ) {
            continue;
          }

          let row            = yields[cdrIndex];
          let lineMetricsRow = res.data.LineMetrics[cdrIndex];

          for (let matrixCprIndex = 0; matrixCprIndex < matrixCpr.length; matrixCprIndex++) {
            if (
                matrixCpr[matrixCprIndex] === '' ||
                matrixCpr[matrixCprIndex] == undefined
            ) {
              continue;
            }

            let yieldResult       = row[cprIndex];
            let lineMetricsResult = lineMetricsRow[cprIndex];

            let value = '';
            if (yieldResult.IsSuccessful) {
              value = formatter.formatPctValue(yieldResult.Value, 2);
            } else {
              value = '-----';
              // TODO sk: remove after redesigning c++ message system
              if (
                  yieldResult.ErrorMessage !== "Can't compute Yield." &&
                  yieldResult.ErrorMessage !== 'Too many tries in Yield calculation.' &&
                  yieldResult.ErrorMessage !== 'Guess is too negative in Yield calculation.' &&
                  yieldResult.ErrorMessage !== "Can't compute Spread." &&
                  yieldResult.ErrorMessage !== 'Too many tries in Spread calculation.'
              ) {
                errorMessage = yieldResult.ErrorMessage;
              }
            }
            roiMatrix[matrixCprIndex][matrixCdrIndex]          = value;
            rateOnLineMatrix[matrixCprIndex][matrixCdrIndex]   = formatter.formatPctValue(
                lineMetricsResult.RateOnLine,
                2
            );
            lossOnLineMatrix[matrixCprIndex][matrixCdrIndex]   = formatter.formatPctValue(
                lineMetricsResult.LossOnLine,
                2
            );
            returnOnLineMatrix[matrixCprIndex][matrixCdrIndex] = formatter.formatPctValue(
                lineMetricsResult.ReturnOnLine,
                2
            );
            cprIndex++;
          }
          cprIndex = 0;
          cdrIndex++;
        }
        return {
          errorMessage,
          matrixCdr,
          matrixCpr,
          roiMatrix,
          rateOnLineMatrix,
          lossOnLineMatrix,
          returnOnLineMatrix
        };

      } catch (err) {
        roiMatrix          = createEmpty2dArray(8);
        rateOnLineMatrix   = createEmpty2dArray(8);
        lossOnLineMatrix   = createEmpty2dArray(8);
        returnOnLineMatrix = createEmpty2dArray(8);
        await service.log(
            TRACE_ERROR,
            err.response && err.response.data ? err.response.data.Message : 'Error occurred', err.stack
        );
        throw err;
      }
    },

    async runAllBondsCalculation(flagCallFromStructureComponent = false) {
      if (!this.structure || !this.structure.Bonds) {
        await this.showStructure();
      }
      this.cashflowAllBonds  = [];
      const structure        = this.structure;
      const cashflowAllBonds = [];
      const dealName         = this.dealName;
      const isMatrixClicked  = this.isMatrixClicked;
      const cprToPass        = this.cprToPass;
      const cdrToPass        = this.cdrToPass;
      let response           = null;
      let eachBond           = null;
      // ##################################################
      for (let bond of structure.Bonds) {
        const ownedFaceAmt = flagCallFromStructureComponent ? bond.OwnedFaceAmt : this.inputOwnedFaceAmt;
        const margin       = flagCallFromStructureComponent ? bond.Margin : this.inputMargin;
        if (UtilsData.isEmpty(UtilsData.castToNumber(ownedFaceAmt))) {
          continue;
        }
        response = await this.runCalculation(
            true,
            null,
            null,
            bond.AcisBondName,
            ownedFaceAmt,
            margin
        );
        eachBond = {
          acisCashflow:    response.acisCashflow,
          totals:          response.totals,
          dealName:        dealName,
          bondName:        bond.AcisBondName,
          isMatrixClicked: isMatrixClicked,
          cpr:             cprToPass,
          cdr:             cdrToPass,
          miscVariables:   response.miscVariables
        }
        cashflowAllBonds.push(eachBond);
      }
      // ##################################################
      this.cashflowAllBonds = cashflowAllBonds;
    },

    // ##################################################

    onMatrixCellClicked(payload) {
      const {cpr, cdr}       = payload;
      this.matrixCellClicked = true;
      this.cprToPass         = cpr;
      this.cdrToPass         = cdr;
      this.runCalculation(true, cpr, cdr);
    },

    async showStructure() {
      ObjSpinnerState.isOn = true;
      let res              = await service.getAcisDealStructure(this.dealName);
      if (res.status == 200) {
        for (let bond of res.data.Bonds) {
          bond.OriginalBalance = formatter.formatDollarValue(bond.OriginalBalance);
          bond.CurrentBalance  = formatter.formatDollarValue(bond.CurrentBalance);
          bond.Factor          = formatter.round(bond.Factor);
          bond.Premium         = formatter.formatPctValue(bond.Premium);
        }
        this.structure = res.data;
      } else {
        this.errorMessage = 'Failed to load deal structure.';
      }
      ObjSpinnerState.isOn = false;
    },

    clearErrorMessage() {
      this.errorMessage = '';
    },
    clearResults() {
      this.acisCashflow    = [];
      this.yield           = '';
      this.wal             = '';
      this.principalWindow = '';
      this.writedownPct    = '';
    },
    exportCashflowToExcel() {
      return excelSupport.exportCashflowToExcel(
          this.$store.getters.username,
          this.dealName,
          this.bondName,
          this.settlementDate,
          this.runDate,
          this.selectedScenario ? this.selectedScenario.data : null,
          this.$refs.cashflow.toMatrix()
      );
    },

    exportCashflowAllBondsToExcel() {
      const userName         = this.$store.getters.username;
      const dealName         = this.dealName;
      const settlementDate   = this.settlementDate;
      const runDate          = this.runDate;
      const selectedScenario = this.selectedScenario ? this.selectedScenario.data : null;
      const cashflowAllBonds = this.cashflowAllBonds;
      const arr              = [];
      const refs             = this.$refs['refAllBondsCashflow'];
      const fileName         = `${dealName} - All Bonds Calculation.xlsx`;
      let el                 = null;
      let data               = [];
      for (let [i, eachBond] of Object.entries(cashflowAllBonds)) {
        data = [
          ['UserName', userName],
          ['Deal Name', dealName],
          ['Bond Name', eachBond.bondName || '-----'],
          ['Settlement Date', settlementDate],
          ['Run Date', runDate.toLocaleDateString()],
          ['Run Time', runDate.toLocaleTimeString()],
          ['Scenario name', selectedScenario ? selectedScenario.ScenarioName : '-----'],
          ['Scenario vendor', selectedScenario ? selectedScenario.Vendor : '-----'],
          []
        ];
        data = data.concat(refs[i].toMatrix())
        el   = {
          sheetName: eachBond.bondName,
          sheetData: data
        };
        arr.push(el);
      }
      return excelSupport.exportToXlsx(arr, fileName);
    }

  },
  components: {
    SingleScenarioResult,
    LineMetrics,
    Settings,
    Cashflow,
    Matrix,
    AcisStructure
  }
};

function readCookies(root) {
  root.inputOwnedFaceAmt = root.$cookies.isKey('acisOwnedFaceAmt')
      ? root.$cookies.get('acisOwnedFaceAmt')
      : '1,000,000';
  root.inputMargin       = root.$cookies.isKey('acisMargin') ? root.$cookies.get('acisMargin') : 20;
  root.inputInterestRate = root.$cookies.isKey('acisInterestRate')
      ? root.$cookies.get('acisInterestRate')
      : 0.0;
  root.matrixCpr         = root.$cookies.isKey('acisMatrixCpr')
      ? JSON.parse(root.$cookies.get('acisMatrixCpr'))
      : [0, 5, 10, 15, 20, 25, 30, 35];
  root.matrixCdr         = root.$cookies.isKey('acisMatrixCdr')
      ? JSON.parse(root.$cookies.get('acisMatrixCdr'))
      : [0, 0.25, 0.5, 1, 1.5, 2, 2.5, 3, 5];
}

function setDataFromAppData(root) {
  root.yield              = root.$store.getters.acisSingleResult.yield;
  root.totals             = root.$store.getters.acisSingleResult.acisCashflowTotals;
  root.acisCashflow       = root.$store.getters.acisSingleResult.acisCashflow;
  root.wal                = root.$store.getters.acisSingleResult.wal;
  root.principalWindow    = root.$store.getters.acisSingleResult.principalWindow;
  root.writedownPct       = root.$store.getters.acisSingleResult.writedownPct;
  root.firstLossPeriod    = root.$store.getters.acisSingleResult.firstLossPeriod;
  root.lineMetrics        = root.$store.getters.acisSingleResult.lineMetrics;
  root.roiMatrix          = isNotEmpty2dArray(root.$store.getters.acisMatrixResult.roiMatrix)
      ? root.$store.getters.acisMatrixResult.roiMatrix
      : createEmpty2dArray(8);
  root.rateOnLineMatrix   = isNotEmpty2dArray(root.$store.getters.acisMatrixResult.rateOnLineMatrix)
      ? root.$store.getters.acisMatrixResult.rateOnLineMatrix
      : createEmpty2dArray(8);
  root.lossOnLineMatrix   = isNotEmpty2dArray(root.$store.getters.acisMatrixResult.lossOnLineMatrix)
      ? root.$store.getters.acisMatrixResult.lossOnLineMatrix
      : createEmpty2dArray(8);
  root.returnOnLineMatrix = isNotEmpty2dArray(
      root.$store.getters.acisMatrixResult.returnOnLineMatrix
  )
      ? root.$store.getters.acisMatrixResult.returnOnLineMatrix
      : createEmpty2dArray(8);
}

function setCookies(root) {
  if (root.inputOwnedFaceAmt) {
    root.$cookies.set('acisOwnedFaceAmt', root.inputOwnedFaceAmt, '10d');
  }
  if (root.inputMargin) {
    root.$cookies.set('acisMargin', root.inputMargin, '10d');
  }
  if (root.inputInterestRate) {
    root.$cookies.set('acisInterestRate', root.inputInterestRate, '10d');
  }
  if (root.matrixCpr.length) {
    root.$cookies.set('acisMatrixCpr', JSON.stringify(root.matrixCpr), '10d');
  }
  if (root.matrixCdr.length) {
    root.$cookies.set('acisMatrixCdr', JSON.stringify(root.matrixCdr), '10d');
  }
  if (root.hpi.length) {
    root.$cookies.set('acisHpi', root.hpi, '10d');
  }
  if (root.NationalUnemploymentForecast.length) {
    root.$cookies.set('acisNationalUnemploymentForecast', root.NationalUnemploymentForecast, '10d');
  }
  if (root.hpa.length) {
    root.$cookies.set('acisHpa', root.hpa, '10d');
  }
  if (root.twoYrTsy.length) {
    root.$cookies.set('acis2YrTsy', root.twoYrTsy, '10d');
  }
  if (root.tenYrTsy.length) {
    root.$cookies.set('acis10YrTsy', root.tenYrTsy, '10d');
  }
  if (root.tuneCpr.length) {
    root.$cookies.set('acisTuneCpr', root.tuneCpr, '10d');
  }
  if (root.tuneCdr.length) {
    root.$cookies.set('acisTuneCdr', root.tuneCdr, '10d');
  }
  if (root.tuneSeverity.length) {
    root.$cookies.set('acisTuneSeverity', root.tuneSeverity, '10d');
  }
}
</script>

<style lang="scss" scoped>
table {
  &.color {
    margin-bottom: 0;

    th {
      vertical-align: middle;
    }
  }

  &.no-border {
    th,
    td {
      border: none;
    }
  }
}

.form-control {
  text-align: right;
}

.custom-select {
  border-radius: 0;
}

.no-focus {
  &:focus {
    outline: none;
  }
}
</style>
